import React, { useEffect, useState } from 'react';
import { Descriptions, Spin, Divider, message, Grid } from 'antd';
import type {
  BaseEvaluationReport,
  EvaluationReportForComputerScience,
  EvaluationReportForMathAndMathComputerScience,
} from '../../../Types/interfaces';
import {
  PaperWorkLanguage,
  StepName,
  StudyLineType,
} from '../../../Types/interfaces';
import {
  GetEvaluationReportById,
  GetOrganizationById,
} from '../../../helper/apiHelper';
import { useTranslation } from 'react-i18next';
import {
  calcKnowledgePoints,
  calcSoftSkillPoints,
  formatNumberWithMaxDecimals,
  getQualitiesGrade,
  getSoftSkillGrade,
} from '../utils/calculations';
import { formatDate } from '../../../Utils/date';
import { MathAndMathComputerScienceEvaluationReportDisplayComponent } from './MathAndMathComputerScienceEvaluationReportDisplayComponent';
import { ComputerScienceEvaluationReportDisplayComponent } from './ComputerScienceEvaluationReportDisplayComponent';
import type { JSX } from 'react';
import FileUploader from '../../FileUploader';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../../Constants/queryKeys';

const { useBreakpoint } = Grid;

interface EvaluationReportDisplayProps {
  reportId: number;
  organizationId: number;
  isSignable?: boolean;
  studentName: string;
}

export const EvaluationReportDisplay = ({
  reportId,
  organizationId,
  isSignable = true,
}: EvaluationReportDisplayProps): JSX.Element => {
  const [messageApi] = message.useMessage();
  const [loading, setLoading] = useState(true);
  const [evaluationReport, setEvaluationReport] =
    useState<BaseEvaluationReport | null>(null);
  const { t } = useTranslation();
  const [language, setLanguage] = useState<PaperWorkLanguage>();
  const queryClient = useQueryClient();
  const screens = useBreakpoint();

  useEffect(() => {
    (async (): Promise<void> => {
      if (!organizationId) {
        return;
      }
      const orgResp = await GetOrganizationById(organizationId.toString());
      setLanguage(orgResp.language);
    })();
  }, [organizationId]);

  useEffect(() => {
    const fetchEvaluationReport = async (): Promise<void> => {
      if (!reportId || reportId === 0) {
        return;
      }
      try {
        const response = await GetEvaluationReportById(reportId);

        setEvaluationReport(response);
        setLoading(false);
      } catch (error) {
        messageApi.error({
          content: t('evalReportDisplay.notFoundErr') + error,
        });
        setLoading(false);
      }
    };

    fetchEvaluationReport();
  }, [reportId, messageApi, t]);

  if (loading) {
    return <Spin size="large" />;
  }

  if (!evaluationReport) {
    return <div>{t('evalReportDisplay.notFound')}</div>;
  }

  const softSkillPoints = calcSoftSkillPoints(evaluationReport);
  let MainActivityOrObjectiveInternshipElement: React.ReactNode;
  let SpecificReportElement: React.ReactNode;
  let knowledgePoints: number = 0;
  let finalGrade: number = 0;

  switch (evaluationReport.studyLineType) {
    case StudyLineType.ComputerScience: {
      const evalReportForComputerScience =
        evaluationReport as EvaluationReportForComputerScience;
      SpecificReportElement = (
        <ComputerScienceEvaluationReportDisplayComponent
          evaluationReport={evalReportForComputerScience}
        />
      );
      knowledgePoints = calcKnowledgePoints(
        evaluationReport as EvaluationReportForComputerScience
      );
      finalGrade = 1 + knowledgePoints + softSkillPoints;

      MainActivityOrObjectiveInternshipElement = (
        <Descriptions title={t('evalReportDisplay.mainActTit')} bordered>
          <Descriptions.Item label={t('evalReportDisplay.mainAct')}>
            {evalReportForComputerScience.organizationMainAcitvityIsSoftwareDevelopment &&
              t('evalReportDisplay.softDev')}
            {evalReportForComputerScience.organizationMainAcitvityIsSoftwareDevelopmentUtilization &&
              t('evalReportDisplay.softUtil')}
            {evalReportForComputerScience.organizationMainAcitvityIsITServicesAndEquipment &&
              t('evalReportDisplay.itServ')}
            {evaluationReport.otherMainActivity}
          </Descriptions.Item>
        </Descriptions>
      );
      break;
    }

    case StudyLineType.Math:
    case StudyLineType.MathComputerScience: {
      const evalReportForMathAndMathComputerScience =
        evaluationReport as EvaluationReportForMathAndMathComputerScience;
      SpecificReportElement = (
        <MathAndMathComputerScienceEvaluationReportDisplayComponent
          evaluationReport={evalReportForMathAndMathComputerScience}
        />
      );
      knowledgePoints = calcKnowledgePoints(
        evaluationReport as EvaluationReportForMathAndMathComputerScience
      );
      finalGrade = 1 + knowledgePoints + softSkillPoints;

      MainActivityOrObjectiveInternshipElement = (
        <Descriptions
          title={t('evalReportDisplay.math.internshipObjective')}
          bordered
        >
          <Descriptions.Item
            label={t('evalReportDisplay.math.internshipObjective')}
          >
            {evalReportForMathAndMathComputerScience.mainActivityForFundamentalMathematicalStructures &&
              t('evalReportDisplay.math.mathematicalStructures')}
            {evalReportForMathAndMathComputerScience.mainActivityForAlgebraAnalysisGeometryAndDifferentialEquations &&
              t(
                'evalReportDisplay.math.algebraAnalysisGeometryAndDifferentialEquations'
              )}
            {evalReportForMathAndMathComputerScience.mainActivityMathModelareMath &&
              t('evalReportDisplay.math.mathematicaModeling')}
            {evalReportForMathAndMathComputerScience.mainActivityMathProbability &&
              t('evalReportDisplay.math.probabilityAndStatistics')}
            {evalReportForMathAndMathComputerScience.mainActivityMathMethodologyAndPedagogy &&
              t('evalReportDisplay.math.teachingMethodologyAndDidactics')}
            {evaluationReport.otherMainActivity}
          </Descriptions.Item>
        </Descriptions>
      );
      break;
    }

    default:
      SpecificReportElement = <div>{t('evalReportDisplay.notFound')}</div>;
      break;
  }

  return (
    <div>
      <Descriptions title={t('evalReportDisplay.duration')} bordered column={1}>
        <Descriptions.Item label={t('evalReportDisplay.durationStart')}>
          {evaluationReport.internshipStarted
            ? formatDate(evaluationReport.internshipStarted)
            : ''}
        </Descriptions.Item>
        <Descriptions.Item label={t('evalReportDisplay.durationEnd')}>
          {evaluationReport.internshipCompleted
            ? formatDate(evaluationReport.internshipCompleted)
            : ''}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      {MainActivityOrObjectiveInternshipElement}

      <Divider />

      <Descriptions
        title={t('evalReportDisplay.includedAct')}
        bordered
        column={1}
      >
        <Descriptions.Item label={t('evalReportDisplay.incTraining')}>
          {evaluationReport.internshipIncludedTraining
            ? t('evalReportDisplay.yes')
            : t('evalReportDisplay.no')}
        </Descriptions.Item>
        <Descriptions.Item label={t('evalReportDisplay.incSoftDev')}>
          {evaluationReport.internshipIncludedSoftwareDevelopment
            ? t('evalReportDisplay.yes')
            : t('evalReportDisplay.no')}
        </Descriptions.Item>
        <Descriptions.Item label={t('evalReportDisplay.incDevOfSoftSkills')}>
          {evaluationReport.internshipIncludedDevelopmentOfSoftSkills
            ? t('evalReportDisplay.yes')
            : t('evalReportDisplay.no')}
        </Descriptions.Item>
        <Descriptions.Item label={t('evalReportDisplay.incEval')}>
          {evaluationReport.internshipIncludedEvaluation
            ? t('evalReportDisplay.yes')
            : t('evalReportDisplay.no')}
        </Descriptions.Item>
        {evaluationReport.otherActvitiesIncludedInInternship && (
          <Descriptions.Item label={t('evalReportDisplay.otherAct')}>
            {evaluationReport.otherActvitiesIncludedInInternship}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider />

      {SpecificReportElement}

      <br />
      <Descriptions bordered size="small" style={{ maxWidth: 300 }}>
        <Descriptions.Item label={t('evalReportDisplay.ptsForKnowledge')}>
          {formatNumberWithMaxDecimals(knowledgePoints)} / 6
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <Descriptions
        title={t('evalReportDisplay.softSkills')}
        bordered
        column={screens.xl ? 2 : 1}
      >
        <Descriptions.Item label={t('evalReportDisplay.compWithTasks')}>
          {getSoftSkillGrade(evaluationReport.taskAdherence, t)}
        </Descriptions.Item>
        <Descriptions.Item label={t('evalReportDisplay.respectDeadlines')}>
          {getSoftSkillGrade(evaluationReport.terminologyUsage, t)}
        </Descriptions.Item>
        {language === PaperWorkLanguage.English && (
          <Descriptions.Item label={t('evalReportDisplay.background')}>
            {getQualitiesGrade(evaluationReport.pointsForBackground, t)}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t('evalReportDisplay.teamwork')}>
          {getQualitiesGrade(evaluationReport.pointsForTeamWork, t)}
        </Descriptions.Item>
        <Descriptions.Item label={t('evalReportDisplay.resp')}>
          {getQualitiesGrade(evaluationReport.pointsForResponsibility, t)}
        </Descriptions.Item>
        <Descriptions.Item label={t('evalReportDisplay.initiative')}>
          {getQualitiesGrade(evaluationReport.pointsForInitiative, t)}
        </Descriptions.Item>
        <Descriptions.Item label={t('evalReportDisplay.effort')}>
          {getQualitiesGrade(evaluationReport.pointsForEffortCapacity, t)}
        </Descriptions.Item>
        <Descriptions.Item label={t('evalReportDisplay.adaptAndImprove')}>
          {getQualitiesGrade(evaluationReport.pointsForAdaptationCapacity, t)}
        </Descriptions.Item>
        {evaluationReport.pointsForOtherSoftSkills !== 0 && (
          <Descriptions.Item
            label={
              t('evalReportDisplay.other') +
              ': ' +
              evaluationReport.otherSoftSkills
            }
          >
            {getQualitiesGrade(evaluationReport.pointsForOtherSoftSkills, t)}
          </Descriptions.Item>
        )}
      </Descriptions>
      <br />
      <Descriptions bordered size="small" style={{ maxWidth: 300 }}>
        <Descriptions.Item label={t('evalReportDisplay.ptsForSoftSkills')}>
          {formatNumberWithMaxDecimals(calcSoftSkillPoints(evaluationReport))} /
          3
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <Descriptions size="small" bordered style={{ maxWidth: 300 }}>
        <Descriptions.Item label={t('evalReportDisplay.finalGrade')}>
          {formatNumberWithMaxDecimals(finalGrade)}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      {isSignable && (
        <FileUploader
          apiUrl={'evaluation-reports'}
          documentUrl={evaluationReport.evaluationDocumentUrl}
          documentSigningStatus={StepName.signingEvaluationReportByTutor}
          isSignatureDisabled={false}
          alreadySigned={false}
          onFileUpload={async () => {
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
            });
          }}
        />
      )}
    </div>
  );
};
